import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { isChunkLoadError, recoverFromChunkLoadError } from "@/system/chunkError";

/**
 * lazy() that self-heals the stale-chunk-after-deploy failure.
 *
 * The first stale-chunk signal starts a cache-busting navigation. A per-route
 * session flag plus the global recovery window prevents infinite reload loops
 * if the requested chunk is genuinely broken rather than merely stale.
 *
 * Drop-in replacement for React.lazy at the call sites in App.tsx.
 */
const RELOAD_FLAG_PREFIX = "nuvra:chunk-reload:";

function readFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_PREFIX + key) === "1";
  } catch {
    return false;
  }
}

function setFlag(key: string): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG_PREFIX + key, "1");
  } catch {
    /* sessionStorage unavailable: the route boundary still handles the failure. */
  }
}

function clearFlag(key: string): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_PREFIX + key);
  } catch {
    /* ignore */
  }
}

// Exported for unit testing the recovery decision without React/Suspense.
export async function loadWithReload<T>(
  factory: () => Promise<T>,
  key: string,
  reload: () => void = () => window.location.reload(),
): Promise<T> {
  try {
    const mod = await factory();
    clearFlag(key); // recovered (or never broke): reset so a future deploy can heal again
    return mod;
  } catch (err) {
    if (isChunkLoadError(err) && !readFlag(key)) {
      setFlag(key);

      // recoverFromChunkLoadError performs the actual cache-busting navigation.
      // If the Vite global listener already handled this exact failure, it returns
      // true without starting another navigation. Either way Suspense stays
      // pending while the browser moves to the fresh application shell.
      if (recoverFromChunkLoadError(err, reload)) {
        return new Promise<T>(() => {});
      }

      clearFlag(key);
    }

    throw err;
  }
}

export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  key: string,
): LazyExoticComponent<T> {
  return lazy(() => loadWithReload(factory, key));
}
